import {
  createContext,
  PropsWithChildren,
  useContext,
  useLayoutEffect,
  useReducer,
  useState,
} from "react";
import {
  DesignerApplication,
  DesignerUser,
  HomeOwnerUser,
  MutationCreateNewDesignerAccountArgs,
  MutationCreateNewHomeownerAccountArgs,
  MutationLoginUserArgs,
  MutationUpdateDesignerAccountArgs,
  MutationUpdateHomeownerAccountArgs,
  UserAccountType,
  UserFromServer,
} from "../../__generated__/types";
import { useCreateDesignerAccountMutation } from "../useCreateDesignerAccountMutation";
import { useCreateHomeownerAccountMutation } from "../useCreateHomeownerAccountMutation";
import { useUpdateDesignerAccountMutation } from "../useUpdateDesignerAccountMutation";
import { useUpdateHomeownerAccountMutation } from "../useUpdateHomeownerAccountMutation";
import { useLoginMutation } from "../useLoginMutation";
import { useNavigate } from "react-router";
import { UserContextActions, UserContextDispatchType, AccountCreationResult } from "./types";
import {
  mergeUserWithPersistedMembershipState,
  USER_CONTEXT_INITIAL_STATE,
  userContextReducer,
} from "./utils";
import { CircularProgress } from "@mui/material";
import { useConfirmEmailMutation } from "../useConfirmEmailMutation";
import { useResetPasswordMutation } from "../useResetPasswordMutation";
import { Pages } from "../../utils";
import type { MembershipStatusSnapshot } from "./types";

const UserContext = createContext<{
  user: UserFromServer | null;
  token: string | null;
  loading: boolean;
  applicationData: DesignerApplication | null;
  refreshUserData?: () => void;
  logoutUserAccount?: () => Promise<void>;
}>(USER_CONTEXT_INITIAL_STATE);
const UserContextDispatch = createContext<UserContextDispatchType>({} as any);

const normalizeSubscriptionValue = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  if (value && typeof value === "object") {
    const wrapped = value as
      | { designerValue?: unknown; homeownerValue?: unknown; value?: unknown; tier?: unknown }
      | null;
    const candidate =
      wrapped?.designerValue ??
      wrapped?.homeownerValue ??
      wrapped?.value ??
      wrapped?.tier;
    if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      return trimmed ? trimmed : null;
    }
  }

  return null;
};

export const UserContextProvider = ({ children }: PropsWithChildren) => {
  const [state, setState] = useReducer(userContextReducer, {
    ...USER_CONTEXT_INITIAL_STATE,
  });

  const navigate = useNavigate();


  const { createDesignerAccount, loading: loadingCreateDesigner } =
    useCreateDesignerAccountMutation();

  const { createHomeownerAccount, loading: loadingCreateHomeowner } =
    useCreateHomeownerAccountMutation();

  const { updateDesignerAccount, loading: loadingDesignerUpdate } =
    useUpdateDesignerAccountMutation();

  const { updateHomeownerAccount, loading: loadingHomeownerUpdate } =
    useUpdateHomeownerAccountMutation();

  const { handleConfirmEmail } = useConfirmEmailMutation();
  const { handleResetPassword } = useResetPasswordMutation(); // Assuming you have a reset password mutation

  const {
    loginUser,
    // error: loginError,
    loading: loginLoading,
  } = useLoginMutation();

  const setUser = (user: Partial<DesignerUser | HomeOwnerUser> | null) => {
    if (!user) {
      setState({ type: UserContextActions.SET_USER, payload: null });
      localStorage.setItem("user", JSON.stringify(null));
      return;
    }

    let normalizedUser = user;
    if (Object.prototype.hasOwnProperty.call(user, "subscription")) {
      normalizedUser = {
        ...user,
        subscription: normalizeSubscriptionValue((user as any).subscription),
      } as Partial<DesignerUser | HomeOwnerUser>;
    }

    let persistedUser: Record<string, unknown> | null = null;
    const currentUser = state.user as Record<string, unknown> | null;
    if (currentUser) {
      persistedUser = currentUser;
    } else {
      try {
        const storedUser = localStorage.getItem("user");
        const parsedUser = storedUser ? JSON.parse(storedUser) : null;
        persistedUser =
          parsedUser && typeof parsedUser === "object"
            ? (parsedUser as Record<string, unknown>)
            : null;
      } catch (error) {
        persistedUser = null;
      }
    }

    normalizedUser = mergeUserWithPersistedMembershipState(
      normalizedUser as Record<string, unknown>,
      persistedUser
    ) as Partial<DesignerUser | HomeOwnerUser>;

    setState({ type: UserContextActions.SET_USER, payload: normalizedUser });
    localStorage.setItem("user", JSON.stringify(normalizedUser));
  };

  const createNewDesignerAccount = async (
    args: MutationCreateNewDesignerAccountArgs
  ): Promise<AccountCreationResult> => {
    try {
      const result = await createDesignerAccount({
        variables: args,
      });
      if (result?.data?.createNewDesignerAccount) {
        setUser({
          id: result.data.createNewDesignerAccount,
          email: args.email,
          accountType: UserAccountType.Designer,
          emailVerified: 'false',
          onboardingStatus: 'pending_email_verification',
        } as Partial<DesignerUser>);
      }
      return { success: true, data: result?.data?.createNewDesignerAccount };
    } catch (error: any) {
      console.error('Designer account creation error:', error);
      const errorMessage = error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to create designer account';
      return { success: false, error: errorMessage };
    }
  };

  const createNewHomeownerAccount = async (
    args: MutationCreateNewHomeownerAccountArgs
  ): Promise<AccountCreationResult> => {
    try {
      const result = await createHomeownerAccount({
        variables: args,
      });
      if (result?.data?.createNewHomeownerAccount) {
        setUser({
          id: result.data.createNewHomeownerAccount,
          email: args.email,
          accountType: UserAccountType.Homeowner,
          emailVerified: 'false',
          onboardingStatus: 'pending_email_verification',
        } as Partial<HomeOwnerUser>);
      }
      return { success: true, data: result?.data?.createNewHomeownerAccount };
    } catch (error: any) {
      console.error('Homeowner account creation error:', error);
      const errorMessage = error?.message || error?.graphQLErrors?.[0]?.message || 'Failed to create homeowner account';
      return { success: false, error: errorMessage };
    }
  };

      const updateDesignerAccountData = async (
        args: MutationUpdateDesignerAccountArgs
      ) => {
        console.log("🔍 REGISTRATION DEBUG:", JSON.stringify(args, null, 2));
        try {
          const result = await updateDesignerAccount({
            variables: {
              _id: args._id,
              firstName: args.firstName,
              lastName: args.lastName,
              email: args.email,
              phoneNumber: args.phoneNumber,
              businessAddress: args.businessAddress,
              businessName: args.businessName,
              businessCity: args.businessCity,
              businessState: args.businessState,
              businessZipCode: args.businessZipCode,
              businessWebsite: args.businessWebsite,
              communicationPreference: args.communicationPreference,
              workLocation: args.workLocation,
            },
          });
          console.log("🔍 REGISTRATION RESULT:", JSON.stringify(result, null, 2));
          console.log("🔍 REGISTRATION RESULT onboardingStatus:", result?.onboardingStatus);
          console.log("🔍 REGISTRATION RESULT raw result:", result);
          
          if (result) {
            // Keep the onboardingStatus from backend (should be pending_profile_completion)
            // This ensures the frontend has the correct status from server
            const updatedUser = {
              ...result,
              onboardingStatus: result.onboardingStatus || 'pending_profile_completion'
            };
            
            console.log("🔍 DEBUG: Setting user with updated data:", {
              id: updatedUser.id,
              email: updatedUser.email,
              accountType: updatedUser.accountType,
              onboardingStatus: updatedUser.onboardingStatus
            });
            setUser(updatedUser);
          }
          return result;
        } catch (error: any) {
          console.error("🔍 REGISTRATION ERROR:", error);
          console.error("🔍 REGISTRATION ERROR DETAILS:", {
            message: error?.message,
            graphQLErrors: error?.graphQLErrors,
            networkError: error?.networkError,
            extraInfo: error?.extraInfo
          });
          throw error;
        }
      };

  const updateHomeownerAccountData = async (
    args: MutationUpdateHomeownerAccountArgs
  ) => {
    const result = await updateHomeownerAccount({
      variables: args,
    });
    const updatedUser = result?.data?.updateHomeownerAccount;
    if (updatedUser) {
      const subscriptionWrapper = updatedUser.subscription as any;
      const normalizedSubscription =
        subscriptionWrapper?.designerValue ||
        subscriptionWrapper?.homeownerValue ||
        subscriptionWrapper?.value ||
        updatedUser.subscription;
      const currentUser = state.user as (Partial<DesignerUser | HomeOwnerUser> & {
        onboardingStatus?: string | null;
        paymentStatus?: string | null;
        subscription?: unknown;
      }) | null;
      setUser({
        ...(currentUser ?? {}),
        ...updatedUser,
        subscription: normalizedSubscription ?? currentUser?.subscription,
        onboardingStatus:
          updatedUser.onboardingStatus ?? currentUser?.onboardingStatus,
        paymentStatus: updatedUser.paymentStatus ?? currentUser?.paymentStatus,
      });
    }
    return updatedUser;
  };

      const loginUserAccount = async (args: MutationLoginUserArgs) => {
        console.log("🔍 LOGIN DEBUG:", JSON.stringify(args, null, 2));
        let result;
        try {
          result = await loginUser(args);
          console.log("🔍 LOGIN RESULT:", JSON.stringify(result, null, 2));
        } catch (error) {
          console.error("🔍 LOGIN ERROR:", error);
          return {
            error:
              (error as unknown as any)?.message ??
              "An error occurred during login.",
          };
        }

        if (result) {
          setUser(result);
          if ((result.accountType as string) === "Admin") {
            navigate("/admin-dashboard");
          } else if (result.accountType === UserAccountType.Designer) {
            navigate("/dashboard");
          } else {
            navigate("/");
          }
        }
      };

  const confirmUserEmail = async (token: string, email?: string) => {
    const emailToUse = email || state.user?.email;
    if (!emailToUse) {
      return { error: "Email is required for verification" };
    }
    
    const result = await handleConfirmEmail(emailToUse, token);
    // Result is now the user ID (string) if successful, or null/false if failed
    if (result && typeof result === 'string' && result.trim() !== '') {
      // Email verified successfully, result is the user ID
      setUser({
        ...state.user,
        id: result, // Set the user ID from the backend
        emailVerified: 'true',
        onboardingStatus: 'pending_profile_completion',
      } as Partial<DesignerUser>);
      return result; // Return the user ID
    } else {
      return { error: "Failed to confirm email" }; // Handle error as needed
    }
  };

  const resetUserPassword = async (email: string, password: string) => {
    const result = await handleResetPassword({ email, password });
    return result;
  };

  const syncMembershipStatus = (snapshot?: MembershipStatusSnapshot | null) => {
    if (!snapshot || !state.user) {
      return;
    }

    const currentUser = (state.user ?? {}) as Record<string, any>;
    const currentSubscriptionDetails =
      currentUser.subscriptionDetails && typeof currentUser.subscriptionDetails === "object"
        ? currentUser.subscriptionDetails
        : {};
    const indicatesGuildMembership = [snapshot.subscription, snapshot.currentPlanTier].some(
      (value) => typeof value === "string" && value.toLowerCase().includes("guild")
    );

    const nextSubscriptionDetails = {
      ...currentSubscriptionDetails,
      ...(typeof snapshot.currentPlanTier === "string" && snapshot.currentPlanTier.trim()
        ? { planType: snapshot.currentPlanTier }
      : {}),
      ...(typeof snapshot.billingCycle === "string" && snapshot.billingCycle.trim()
        ? { billingCycle: snapshot.billingCycle }
        : {}),
      ...(typeof snapshot.subscriptionStartDate === "string" &&
      snapshot.subscriptionStartDate.trim()
        ? { startDate: snapshot.subscriptionStartDate }
        : {}),
      ...(typeof snapshot.subscriptionEndDate === "string" &&
      snapshot.subscriptionEndDate.trim()
        ? { endDate: snapshot.subscriptionEndDate }
        : {}),
      ...(indicatesGuildMembership &&
      typeof snapshot.subscriptionStartDate === "string" &&
      snapshot.subscriptionStartDate.trim()
        ? { annualTermStartDate: snapshot.subscriptionStartDate }
        : {}),
      ...(indicatesGuildMembership &&
      typeof snapshot.subscriptionEndDate === "string" &&
      snapshot.subscriptionEndDate.trim()
        ? { annualTermEndDate: snapshot.subscriptionEndDate }
        : {}),
      ...(typeof snapshot.monthlyPlatformFeeNextBillingDate === "string" &&
      snapshot.monthlyPlatformFeeNextBillingDate.trim()
        ? { monthlyPlatformFeeNextBillingDate: snapshot.monthlyPlatformFeeNextBillingDate }
        : {}),
    };

    const nextUser = {
      ...currentUser,
      ...(typeof snapshot.subscription === "string" && snapshot.subscription.trim()
        ? { subscription: snapshot.subscription }
        : {}),
      ...(typeof snapshot.paymentStatus === "string"
        ? { paymentStatus: snapshot.paymentStatus }
        : {}),
      ...(typeof snapshot.billingCycle === "string" && snapshot.billingCycle.trim()
        ? { membershipBillingCycle: snapshot.billingCycle }
        : {}),
      ...(typeof snapshot.autoRenew === "boolean"
        ? { membershipAutoRenew: snapshot.autoRenew }
        : {}),
      ...(typeof snapshot.nextRenewalDate === "string" && snapshot.nextRenewalDate.trim()
        ? { membershipNextRenewalDate: snapshot.nextRenewalDate }
        : {}),
      subscriptionDetails: nextSubscriptionDetails,
    };

    setUser(nextUser as Partial<DesignerUser | HomeOwnerUser>);
  };

  const logoutUserAccount = async () => {
    setUser(null);
    // Application data will be cleared automatically by DesignerApplicationContext
    // when it detects user is null (see useEffect in DesignerApplicationContext)
    navigate(Pages.LOGIN);
  };

  const [localUserCheck, setLocalUserCheck] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Force refresh user data
  const refreshUserData = () => {
    console.log('🔄 Forcing user data refresh...');
    setRefreshKey(prev => prev + 1);
    // Clear any cached data
    localStorage.removeItem('user');
    sessionStorage.clear();
  };

  useLayoutEffect(() => {
    const user = localStorage.getItem("user");
    const localUserData = user ? JSON.parse(user) : null;
    if (!localUserCheck) {
      setLocalUserCheck(true);
      if (localUserData) setUser(localUserData);
    }
  }, [localUserCheck]);

  return (
    <UserContext.Provider
      value={{
        ...state,
        loading: loadingCreateDesigner || loadingDesignerUpdate || loginLoading,
        refreshUserData,
        logoutUserAccount,
      }}
    >
      <UserContextDispatch.Provider
        value={{
          createNewDesignerAccount,
          createNewHomeownerAccount,
          updateDesignerAccountData,
          updateHomeownerAccountData,
          loginUserAccount,
          logoutUserAccount,
          confirmUserEmail,
          resetUserPassword,
          syncMembershipStatus,
        }}
      >
        {!localUserCheck ? <CircularProgress sx={{ color: '#00A06D' }} /> : children}
      </UserContextDispatch.Provider>
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }

  return context;
};

export const useUserContextDispatch = () => {
  const context = useContext(UserContextDispatch);

  if (!context) {
    throw new Error(
      "useUserContextDispatch must be used within a UserContextProvider"
    );
  }

  return context;
};
